import { AssetType } from '@compass/client/types/asset';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { CircleAlert, Clock, Globe, Loader2, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useCaseRecordAssets } from '@/hooks/useCaseRecordAssets';
import { usePortalSession } from '@/hooks/usePortalSession';
import { assetsService } from '@/services/assets.service';
import { useStore } from '@/store/index';

interface PortalPanelProps {
  caseId: string;
  caseDetails: GetCaseRecordResponse | undefined;
  /** Called when a portal session becomes active — parent can collapse the left column, etc. */
  onStartSession?: () => void;
}

/**
 * Renders the full portal submission experience: idle → loading → iframe → complete.
 * Owns all states so the parent just mounts it when the Portal tab is selected.
 * Session cleanup is handled by KnipperCaseSummaryView (the parent) so that
 * tab switches don't tear down the Browserbase session.
 */
export const PortalPanel: React.FC<PortalPanelProps> = ({
  caseId,
  caseDetails,
  onStartSession,
}) => {
  const { getSelectedWorkflow } = useStore();
  const portalName = getSelectedWorkflow()?.name;
  const {
    liveViewUrl,
    isStarting,
    isActive,
    isFillStarted,
    isFormFilled,
    isTimedOut,
    isSubmitted,
    isRecovering,
    expiresAt,
    error,
    startSession,
  } = usePortalSession({ caseId, caseDetails });

  // Notify parent when session becomes active (e.g., collapse left column)
  useEffect(() => {
    if (isActive) {
      onStartSession?.();
    }
  }, [isActive, onStartSession]);

  // NOTE: Session cleanup (unmount + beforeunload) is handled by the parent
  // (KnipperCaseSummaryView) so that switching between Portal/Transcript tabs
  // does not tear down the Browserbase session.

  // Submission complete
  if (isSubmitted) {
    return <SubmissionCompleteView caseId={caseId} />;
  }

  // Session timed out — show restart prompt (check before generic error so timeout gets its own UX)
  if (isTimedOut) {
    const label = portalName ? `Submit in ${portalName} portal` : 'Restart portal session';
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <Globe className="w-12 h-12 mb-4 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground text-sm mb-4">The portal session has expired.</p>
          <Button onClick={startSession} disabled={isStarting}>
            {isStarting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Globe className="w-4 h-4 mr-2" />
            )}
            {label}
          </Button>
        </div>
      </div>
    );
  }

  // Error state — start failure or runtime error
  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <CircleAlert className="w-12 h-12 mb-3 text-destructive mx-auto" />
          <p className="text-destructive font-semibold mb-2">Portal session failed</p>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <Button variant="outline" size="sm" onClick={startSession}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Loading state — session is being created or recovering existing session
  if (isStarting || isRecovering) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mb-4 mx-auto animate-spin text-primary" />
          <p className="text-muted-foreground font-medium">Starting portal session...</p>
          <p className="text-muted-foreground text-sm mt-1">
            Preparing the manufacturer portal form
          </p>
        </div>
      </div>
    );
  }

  // Active session — render iframe.
  // Three phases: login (interactive) → filling (locked) → filled (interactive: captcha/submit).
  //   - fill_started signal flips us from login → filling (agent has signed in, bot is filling)
  //   - form_filled signal flips us from filling → filled (bot done; agent solves captcha & submits)
  if (isActive && liveViewUrl) {
    const isLoggingIn = !isFillStarted && !isFormFilled;
    const isFilling = isFillStarted && !isFormFilled;
    return (
      <div className="flex flex-col h-full min-h-[600px]">
        {isLoggingIn && <PortalLoginBanner />}
        {isFilling && <FormFillingBanner />}
        {isFormFilled && <SessionCountdownBanner expiresAt={expiresAt} />}
        <div className="relative flex-1 h-0">
          <iframe
            src={`${liveViewUrl}&navbar=false`}
            className="absolute inset-0 w-full h-full border-0"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            title="Manufacturer Portal"
          />
          {/*
            Lock the portal ONLY while the bot is actively filling (after login). The login
            phase (isLoggingIn) stays interactive so the agent can sign in on login-gated
            portals (e.g. UCB / IQVIA UltraIntake) — otherwise they'd deadlock: can't type →
            form never appears → fill never starts. Once filled, we unlock again for captcha
            + submit.
          */}
          {isFilling && <div className="absolute inset-0 bg-transparent cursor-not-allowed" />}
        </div>
      </div>
    );
  }

  // Idle — no session started yet
  const label = portalName ? `Submit in ${portalName} portal` : 'Start Portal Session';
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center space-y-4">
        <Globe className="h-12 w-12 text-muted-foreground mx-auto" />
        <div>
          <h3 className="text-lg font-medium">{label}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Opens the manufacturer portal for direct submission
          </p>
        </div>
        <Button onClick={startSession} className="mt-2">
          <Globe className="h-4 w-4 mr-2" />
          {label}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Submission complete — shows confirmation screenshot if available
// ---------------------------------------------------------------------------

const PORTAL_SCREENSHOT_BASE = 'portal_submission_confirmation';

export const SubmissionCompleteView: React.FC<{
  caseId: string;
  /**
   * When true (terminal "Portal Confirmation" tab), render a "no submission"
   * empty state once assets have loaded and no confirmation screenshot exists.
   * When false (live post-submit flow), keep the loading spinner while the
   * just-captured screenshot finishes uploading.
   */
  showEmptyWhenMissing?: boolean;
}> = ({ caseId, showEmptyWhenMissing = false }) => {
  const orgId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  const { data: assets, isLoading } = useCaseRecordAssets(
    caseId,
    AssetType.ASSET_TYPE_CALL_DOCUMENT,
    {
      stopPollingWhenFound: true,
      // In the terminal tab any screenshot was captured long ago, so a single
      // fetch is enough — don't poll forever when a case legitimately has no
      // submission. In the live flow keep the default "poll until an asset
      // appears" so the just-captured screenshot shows up on its own.
      ...(showEmptyWhenMissing ? { isFound: () => true } : {}),
    },
  );

  const screenshotAsset = assets?.find(
    a =>
      a.asset_name?.includes(PORTAL_SCREENSHOT_BASE) ||
      a.file_path?.includes(PORTAL_SCREENSHOT_BASE),
  );

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!screenshotAsset?.id || !orgId || !programId) return;
    let revoked = false;
    assetsService
      .downloadAssetFromArcus(
        { organizationId: orgId, programId, caseRecordId: caseId },
        screenshotAsset.id,
      )
      .then(blob => {
        if (revoked) return;
        setImageUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => {
      revoked = true;
      setImageUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [screenshotAsset?.id, orgId, programId, caseId]);

  // Terminal tab and the case has no confirmation screenshot on record →
  // genuinely no portal submission. (ack_id is intentionally irrelevant: the
  // screenshot, not the ack, is the proof of submission.)
  if (showEmptyWhenMissing && !isLoading && assets && !screenshotAsset) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">
            No portal submission was made for this case.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-auto p-4">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Portal submission confirmation"
          className="max-w-full rounded-lg border shadow-sm"
        />
      ) : (
        <div className="flex items-center justify-center h-full gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading confirmation screenshot…</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filling-in-progress banner — shown while automation is filling the form
// ---------------------------------------------------------------------------

const FormFillingBanner: React.FC = () => (
  <div className="px-4 py-2.5 border-b flex items-center gap-2.5 shrink-0 bg-primary/5 border-primary/20">
    <Loader2 className="w-4 h-4 shrink-0 text-primary animate-spin" />
    <span className="text-sm font-medium flex-1 text-primary">
      Filling out the form — please wait until it&apos;s ready for review.
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Login banner — shown while the agent signs in (before automated filling starts)
// ---------------------------------------------------------------------------

const PortalLoginBanner: React.FC = () => (
  <div className="px-4 py-2.5 border-b flex items-center gap-2.5 shrink-0 bg-muted/40 border-border">
    <Globe className="w-4 h-4 shrink-0 text-muted-foreground" />
    <span className="text-sm font-medium flex-1 text-muted-foreground">
      Sign in to the portal — the form fills automatically once you reach it.
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Countdown banner — shows after automation fills the form
// ---------------------------------------------------------------------------

function useCountdown(expiresAt: string | null): {
  display: string | null;
  secondsLeft: number | null;
} {
  const compute = useCallback((): { display: string | null; secondsLeft: number | null } => {
    if (!expiresAt) return { display: null, secondsLeft: null };
    const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
    if (remaining <= 0) return { display: null, secondsLeft: 0 };
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return { display: `${m}:${s.toString().padStart(2, '0')}`, secondsLeft: remaining };
  }, [expiresAt]);

  const [value, setValue] = useState(compute);

  useEffect(() => {
    if (!expiresAt) return;
    setValue(compute());
    const id = setInterval(() => {
      setValue(compute());
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt, compute]);

  return value;
}

interface SessionCountdownBannerProps {
  expiresAt: string | null;
}

const SessionCountdownBanner: React.FC<SessionCountdownBannerProps> = ({ expiresAt }) => {
  const { display: timeLeft, secondsLeft } = useCountdown(expiresAt);
  const isUrgent = secondsLeft !== null && secondsLeft < 120; // < 2 minutes

  return (
    <div
      className={`
        px-4 py-2.5 border-b flex items-center gap-2.5 shrink-0
        ${
          isUrgent
            ? 'bg-destructive/10 border-destructive/30 animate-pulse'
            : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
        }
      `}
    >
      <Clock
        className={`w-4 h-4 shrink-0 ${isUrgent ? 'text-destructive' : 'text-amber-600 dark:text-amber-400'}`}
      />
      <span
        className={`text-sm font-medium flex-1 ${isUrgent ? 'text-destructive' : 'text-amber-800 dark:text-amber-200'}`}
      >
        Form has been filled. Please review and submit.
      </span>
      {timeLeft && (
        <span
          className={`
            text-sm font-mono font-bold tabular-nums px-2 py-0.5 rounded
            ${
              isUrgent
                ? 'bg-destructive/20 text-destructive'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
            }
          `}
        >
          {timeLeft}
        </span>
      )}
    </div>
  );
};
